import { RedirectDto } from "@intelligems/ig-types/dtos/redirects";
import { ThemeType } from "@intelligems/ig-types/schemas/organization";
import { getExperience } from "@src/api/experiences/get-experience";
import { experienceKeys } from "@src/api/queryKeys";
import { useGetThemes } from "@src/api/shopify-themes/get-themes";
import { useQuery } from "@tanstack/react-query";

/**
 * Resolves the Shopify theme names a theme experience sends traffic to, so the
 * deletion warnings can name them instead of saying "themes used in this test".
 *
 * Theme assignment lives on each variation's theme redirect as a
 * `preview_theme_id` query param, so we join those ids against the org's themes.
 *
 * Returns `[]` when a theme id no longer resolves (the theme was already
 * deleted) or there are no theme redirects yet. Callers fall back to generic
 * copy in that case — see `copy.ts`.
 */
export const resolveThemeNames = (
  redirects: RedirectDto[],
  themes: ThemeType[]
): string[] => {
  const nameById = new Map(
    themes.map((theme) => [String(theme.id), theme.name])
  );

  const names = redirects
    .filter((redirect) => redirect.isThemeTest)
    .map(
      (redirect) =>
        redirect.queryParams.find((param) => param.key === "preview_theme_id")
          ?.value
    )
    .map((themeId) => (themeId ? nameById.get(themeId) : undefined))
    .filter((name): name is string => Boolean(name));

  // Dedupe: variations can share a theme, and the control variation points at
  // the live theme, which we still must not let them delete.
  return [...new Set(names)];
};

export const useThemeExperienceThemeNames = (
  experienceId: string | undefined,
  enabled: boolean
): string[] => {
  // Deliberately not `useGetExperience`: that hook also wires up the 404
  // organization-switch redirect, which pulls routing into every dialog that
  // only wants theme names. The query key is shared, so on the experience page
  // this reads the cache the detail view already populated.
  const experienceQuery = useQuery({
    queryKey: experienceKeys.one(experienceId!),
    queryFn: async () => getExperience(experienceId!),
    enabled: enabled && Boolean(experienceId),
  });
  const themesQuery = useGetThemes();

  const redirects = (experienceQuery.data?.experience.variations ?? []).flatMap(
    (variation) => variation.redirects ?? []
  );

  return resolveThemeNames(redirects, themesQuery.data?.themes ?? []);
};
